import { backgroundNames } from 'constants/backgroundNames';
import { UPCOMING_GAMES_LABEL } from 'constants/names.js';
import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams, useLocation } from 'react-router-dom';
import { getBackgroundName, generatePageTitle } from 'helpers';
import { backgroundImages } from 'assets/img';
import { BackgroundImg } from './style.js';

/**
 * ThemeProvider component that handles theme management for the application.
 * @component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to be rendered with theme context
 * @returns {React.ReactElement} Theme provider wrapper component
 * @example
 * ```jsx
 * <ThemeProvider>
 *   <App />
 * </ThemeProvider>
 * ```
 */
export const ThemeProvider = ({ children }) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const league = searchParams.get('league');
  const collegeLeague = searchParams.get('collegeLeague');
  const { data: configuration } = useSelector((state) => state.configuration);
  const teamApp = useSelector((state) => state.teamApp?.data);
  const games = useSelector((state) => state.games?.data);
  const currentGameIndex = useSelector((state) => state.games?.current);
  const [thisEventLeague, setThisEventLeague] = useState('');
  const [background, setBackground] = useState('');
  const currentBackgroundNameRef = React.useRef('');

  // Skip background rendering on /vitals page (it has its own background)
  const isVitalsPage = location.pathname === '/vitals';

  // Listen for eventLeagueChanged custom event to update background without App re-renders
  useEffect(() => {
    const handleEventLeagueChange = (event) => {
      setThisEventLeague(event.detail.league);
    };

    window.addEventListener('eventLeagueChanged', handleEventLeagueChange);
    return () => {
      window.removeEventListener('eventLeagueChanged', handleEventLeagueChange);
    };
  }, []);

  //** LOAD BACKGROUND IMAGE - STATIC IMPORTS (NO MEMORY LEAKS) */
  const loadBackgroundImage = useCallback((imgName) => {
    let imageName = imgName || backgroundNames.default;

    if (imageName === '') {
      imageName = backgroundNames.default;
    }

    console.log(
      '[ThemeProvider] loadBackgroundImage called with:',
      imgName,
      'resolved to:',
      imageName
    );

    // CRITICAL: Only reload if background actually changes (e.g., NCAAM and NCAAW both use basketball)
    if (currentBackgroundNameRef.current === imageName) {
      console.log(
        '[ThemeProvider] Background already loaded, skipping redundant load'
      );
      return;
    }
    currentBackgroundNameRef.current = imageName;

    const imageUrl =
      backgroundImages[imageName] || backgroundImages[backgroundNames.default];

    console.log(
      '[ThemeProvider] Image URL for',
      imageName,
      ':',
      imageUrl ? 'FOUND' : 'NOT FOUND, using default'
    );

    // Clear previous background first to free memory (critical for iOS Safari)
    setBackground('');

    // Use requestAnimationFrame to ensure DOM update before loading new image
    requestAnimationFrame(() => {
      setBackground(imageUrl);
    });
  }, []);

  //** CHECK IF SITE BACKGROUND IMAGE EXISTS AND IS VALID */
  const getSiteBackgroundImage = useCallback(() => {
    console.log('[ThemeProvider] getSiteBackgroundImage called');
    console.log('[ThemeProvider] Full configuration:', configuration);
    console.log(
      '[ThemeProvider] configuration?.site_background_image:',
      configuration?.site_background_image
    );

    const siteBackgroundName = configuration?.site_background_image;

    if (!siteBackgroundName || siteBackgroundName === '') {
      console.log(
        '[ThemeProvider] No site_background_image configured, using league-based logic'
      );
      return null;
    }

    // Strip file extension from the background image name (e.g., "sport-background.jpg" -> "sport-background")
    const backgroundNameWithoutExtension = siteBackgroundName.split('.')[0];

    // Check if the image exists in our backgroundImages map
    const imageUrl = backgroundImages[backgroundNameWithoutExtension];

    if (imageUrl) {
      console.log(
        '[ThemeProvider] Found valid site_background_image:',
        backgroundNameWithoutExtension
      );
      return imageUrl;
    }

    console.log(
      '[ThemeProvider] site_background_image not found in assets:',
      siteBackgroundName,
      'falling back to league-based logic'
    );
    return null;
  }, [configuration]);

  // Note: Favicon/icon management is now handled by the PWA icon system in App.jsx
  // Icons are automatically updated based on site configuration via updatePageIcons()

  //** INITIALLY SET BACKGROUND FOR DOWNKOUNT SITE **//
  /*
  useEffect(() => {
    if (!league) {
      setBackground(
        loadBackgroundImage(
          configuration?.display_leagues_select === '1'
            ? backgroundNames.default
            : ''
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configuration?.display_leagues_select]);
  */

  //** SET BACKGROUND DEPENDS UPON LEAGUE SELECTED - USE DEFAULT OTHERWISE **//
  useEffect(() => {
    // Skip this effect for one-team sites - they have their own background logic
    const isOneTeamSite =
      configuration?.display_one_team === '1' ||
      configuration?.display_one_team === 1;
    if (isOneTeamSite) {
      return;
    }

    // PRIORITY 1: Check if site has a custom background image configured
    const siteBackgroundUrl = getSiteBackgroundImage();
    if (siteBackgroundUrl) {
      console.log(
        '[ThemeProvider] Using site_background_image, skipping league-based logic'
      );
      // Clear previous background first to free memory (critical for iOS Safari)
      setBackground('');
      // Use requestAnimationFrame to ensure DOM update before loading new image
      requestAnimationFrame(() => {
        setBackground(siteBackgroundUrl);
      });
      return;
    }

    // PRIORITY 2: Fall back to league-based background selection
    let tempLeagueName = '';
    let selectedSource = 'unknown';

    // PRIORITY ORDER: league > collegeLeague > thisEventLeague
    // CRITICAL: When collegeLeague is set, it should LOCK and not be overridden by event changes
    if (league && league != UPCOMING_GAMES_LABEL) {
      // If league is "Favorites", use the actual league from teamApp data
      if (league === 'Favorites' && teamApp?.team_league_name) {
        tempLeagueName = teamApp.team_league_name.toUpperCase();
        selectedSource = 'league (Favorites)';
      } else {
        tempLeagueName = league?.toUpperCase();
        selectedSource = 'league param';
      }
    } else if (collegeLeague && collegeLeague != UPCOMING_GAMES_LABEL) {
      // When collegeLeague is set, ALWAYS use it regardless of event changes
      tempLeagueName = collegeLeague?.toUpperCase();
      selectedSource = 'collegeLeague param (LOCKED)';
    } else if (thisEventLeague) {
      tempLeagueName = thisEventLeague?.toUpperCase();
      selectedSource = 'thisEventLeague (event)';
    }

    console.log(
      '[ThemeProvider] Background selection source:',
      selectedSource,
      'value:',
      tempLeagueName
    );

    const backgroundName = getBackgroundName(tempLeagueName);

    // CRITICAL: Only call loadBackgroundImage if background actually needs to change
    // This prevents iOS Safari crash from excessive function calls on mixed-league pages
    if (currentBackgroundNameRef.current !== backgroundName) {
      loadBackgroundImage(backgroundName);
    }
  }, [
    league,
    collegeLeague,
    loadBackgroundImage,
    thisEventLeague,
    teamApp,
    configuration?.display_one_team,
    getSiteBackgroundImage,
  ]);

  //** FUNCTION TO SET PAGE TITLE AT THE BROWSER WINDOW AND META DESCRIPTION **//
  useEffect(() => {
    if (
      configuration?.site_name &&
      configuration?.sport_level &&
      configuration?.site_description &&
      configuration?.sport_type
    ) {
      generatePageTitle({
        title: `${configuration.site_name} - ${window.location.origin}`,
        metaDescription: `${configuration.site_description ? configuration.site_description : 'The Countdown Continues...'}`,
        //metaDescription: `${configuration.sport_level} ${configuration.sport_type} league game schedule`,
      });
    }
  }, [
    configuration?.site_name,
    configuration?.sport_type,
    configuration?.sport_level,
    configuration?.site_description,
  ]);

  //** SET BACKGROUND FOR ONE TEAM SITES **//
  useEffect(() => {
    if (
      configuration?.display_one_team === '1' ||
      configuration?.display_one_team === 1
    ) {
      // PRIORITY 1: Check if site has a custom background image configured
      const siteBackgroundUrl = getSiteBackgroundImage();
      if (siteBackgroundUrl) {
        console.log(
          '[ThemeProvider] One-team site: Using site_background_image, skipping league-based logic'
        );
        // Clear previous background first to free memory (critical for iOS Safari)
        setBackground('');
        // Use requestAnimationFrame to ensure DOM update before loading new image
        requestAnimationFrame(() => {
          setBackground(siteBackgroundUrl);
        });
        return;
      }

      // PRIORITY 2: Fall back to league-based background selection
      // If user selected a collegeLeague via dropdown, prioritize it over the default
      // Otherwise use the site's one_league_name
      let leagueName = '';

      if (collegeLeague && collegeLeague != UPCOMING_GAMES_LABEL) {
        // User selected a specific league (not "Upcoming Events")
        leagueName = collegeLeague?.toUpperCase();
        console.log(
          '[ThemeProvider] One-team site: using selected collegeLeague:',
          leagueName
        );
      } else if (
        collegeLeague === UPCOMING_GAMES_LABEL &&
        games?.length > 0 &&
        currentGameIndex !== null
      ) {
        // "Upcoming Events" is selected - use the current event's sport/league
        const currentGame = games[currentGameIndex];
        const eventLeague = currentGame?.event_league_name;
        if (eventLeague) {
          leagueName = eventLeague?.toUpperCase();
          console.log(
            '[ThemeProvider] Upcoming Events - using current event league:',
            leagueName
          );
        } else {
          // Fallback to default if event league not found
          leagueName = configuration?.one_league_name?.toUpperCase();
          console.log(
            '[ThemeProvider] Upcoming Events - no event league found, using default:',
            leagueName
          );
        }
      } else {
        // Use site's default league
        leagueName = configuration?.one_league_name?.toUpperCase();
        console.log(
          '[ThemeProvider] One-team site: using default one_league_name:',
          leagueName
        );
      }

      const backgroundName = getBackgroundName(leagueName);
      loadBackgroundImage(backgroundName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    configuration?.display_one_team,
    configuration?.one_league_name,
    collegeLeague,
    games,
    currentGameIndex,
    getSiteBackgroundImage,
  ]);

  console.log(
    '[ThemeProvider] Render - isVitalsPage:',
    isVitalsPage,
    'background exists:',
    !!background
  );

  return (
    <>
      {!isVitalsPage && background ? (
        <BackgroundImg $imgUrl={background} />
      ) : null}
      {children}
    </>
  );
};
